import { Button, Card, Input, message, Popconfirm, Select, Spin, Table, Modal } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
  UndoOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../utils/helper';
import './template-list-oodocs.css';
import EditTemplateOodocs from './EditTemplateOodocs';
import TemplateContentModal from './TemplateContentModal';
import AddTemplateOodocs from '../AddTemplateOodocs';
import DateFormatter from '../../components/DateFormatter';
import ErrorBoundary from '../../components/ErrorBoundary';
import { BASE_API } from '../../axios';
import { TokenStorage } from '../../utils/cookieStorage';

const { Search } = Input;
const { Option } = Select;

const formatFileSize = (bytes: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TemplateListOodocs = () => {
  const [modalitiesList, setModalitiesList] = useState([]);
  const [bodyPartsList, setBodyPartsList] = useState([]);
  const [templatesList, setTemplatesList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [selectedActive, setSelectedActive] = useState('');

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [contentModalVisible, setContentModalVisible] = useState(false);
  const [contentModalMode, setContentModalMode] = useState<'view' | 'edit'>('view');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState(false);

  const isHOD = getUserDetails()?.user_type === 'hod';

  const getTemplatesList = useCallback(() => {
    setTemplatesList({ loading: true, data: [], error: null });
    makePostCall('/template-list-oodocs', { active_only: false })
      .then(res => {
        const templatesData = res.data?.data || [];
        setTemplatesList({ data: templatesData, loading: false, error: null });
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load templates';
        setTemplatesList({ data: [], loading: false, error: errorMessage });
        message.error(`Error loading templates: ${errorMessage}`);
      });
  }, []);

  const getModalitiesList = useCallback(() => {
    makeGetCall('/modalities')
      .then(res => setModalitiesList(res.data?.data || []))
      .catch(e => message.error(`Error loading modalities: ${e.response?.data?.message || e.message}`));
  }, []);

  const getBodyPartsList = useCallback(() => {
    makeGetCall('/body-parts')
      .then(res => setBodyPartsList(res.data?.data || []))
      .catch(e => message.error(`Error loading body parts: ${e.response?.data?.message || e.message}`));
  }, []);

  useEffect(() => {
    getModalitiesList();
    getBodyPartsList();
    getTemplatesList();
  }, [getModalitiesList, getBodyPartsList, getTemplatesList]);

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    let filtered = templatesList.data;

    if (searchText) {
      filtered = filtered.filter(
        (t: any) =>
          t.dt_template_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          t.dt_display_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (selectedModality) {
      filtered = filtered.filter((t: any) => t.dt_modality === selectedModality);
    }
    if (selectedBodyPart) {
      filtered = filtered.filter((t: any) => t.dt_bodypart === selectedBodyPart);
    }
    if (selectedActive) {
      filtered = filtered.filter((t: any) =>
        selectedActive === 'N' ? t.dt_active === 'Y' && t.dt_deleted === 'N' : t.dt_active !== 'Y' || t.dt_deleted === 'Y'
      );
    }
    return filtered;
  }, [templatesList.data, searchText, selectedModality, selectedBodyPart, selectedActive]);

  // Body parts filtered by selected modality
  const filteredBodyPartsList = useMemo(() => {
    if (!selectedModality) {
      return bodyPartsList.filter(
        (item: any, index, self) =>
          index === self.findIndex((bp: any) => (bp.code || bp.value) === (item.code || item.value))
      );
    }
    const availableBodyParts = templatesList.data
      .filter((t: any) => t.dt_modality === selectedModality)
      .map((t: any) => t.dt_bodypart)
      .filter((v, i, self) => self.indexOf(v) === i);

    return bodyPartsList.filter((bp: any) =>
      bp.modality === selectedModality && availableBodyParts.includes(bp.code || bp.value)
    );
  }, [selectedModality, bodyPartsList, templatesList.data]);

  useEffect(() => {
    if (selectedModality) setSelectedBodyPart('');
  }, [selectedModality]);

  const editTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setEditModalVisible(true);
  }, []);

  const openContentModal = useCallback((template, mode: 'view' | 'edit') => {
    setSelectedTemplate(template);
    setContentModalMode(mode);
    setContentModalVisible(true);
  }, []);

  const previewTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setPreviewModalVisible(true);
  }, []);

  const deleteTemplate = useCallback((template, action: string) => {
    const msg = action === 'D' ? 'Template deleted successfully'
      : action === 'A' ? 'Template activated successfully'
      : 'Template deleted permanently';

    setDeleteInProgress(true);
    makePostCall('/delete-template-oodocs', { dt_id: template.dt_id, action })
      .then(res => {
        if (res.data?.success) {
          message.success(msg);
          getTemplatesList();
        } else {
          message.error(res.data?.message || 'Failed to update template');
        }
      })
      .catch(e => message.error(e.response?.data?.message || e.message || 'Failed to update template'))
      .finally(() => setDeleteInProgress(false));
  }, [getTemplatesList]);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedModality('');
    setSelectedBodyPart('');
    setSelectedActive('');
  }, []);

  const getModalityLabel = useCallback(
    (code) => {
      if (!code) return 'N/A';
      const m = modalitiesList.find((m: any) => m.code === code || m.value === code);
      return m ? (m as any).label : code;
    },
    [modalitiesList]
  );

  const getBodyPartLabel = useCallback(
    (code) => {
      if (!code) return 'N/A';
      const bp = bodyPartsList.find((bp: any) => bp.code === code || bp.value === code);
      return bp ? (bp as any).label : code;
    },
    [bodyPartsList]
  );

  const handleDownloadTemplate = useCallback(async (template: any) => {
    try {
      const token = TokenStorage.getAccessToken();
      const res = await fetch(`${BASE_API}/template-url-oodocs/${template.dt_id}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json();
      if (json.success && json.data?.url) {
        const a = document.createElement('a');
        a.href = json.data.url;
        a.download = `${template.dt_template_name || 'template'}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        message.error('Failed to get download URL');
      }
    } catch {
      message.error('Failed to download template');
    }
  }, []);

  const TEMPLATE_COLUMNS = [
    {
      title: 'Template Name',
      key: 'template_name',
      render: (_, record) => record.dt_template_name || 'N/A',
    },
    {
      title: 'Display Name',
      key: 'display_name',
      render: (_, record) => record.dt_display_name || 'N/A',
    },
    {
      title: 'Modality',
      key: 'modality',
      render: (_, record) => getModalityLabel(record.dt_modality),
    },
    {
      title: 'Body Part',
      key: 'bodypart',
      render: (_, record) => getBodyPartLabel(record.dt_bodypart),
    },
    {
      title: 'File Size',
      key: 'file_size',
      render: (_, record) => (
        <span className="file-size-badge">{formatFileSize(record.dt_file_size)}</span>
      ),
    },
    {
      title: 'Added By',
      key: 'added_by',
      render: (_, record) => record.dt_added_by || 'N/A',
    },
    {
      title: 'Added Date',
      key: 'added_dt',
      render: (_, record) => <DateFormatter date={record.dt_created_at} />,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <span className={record.dt_deleted === 'N' && record.dt_active === 'Y' ? 'status-active' : 'status-inactive'}>
          {record.dt_deleted === 'N' && record.dt_active === 'Y' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <div className="action-buttons">
          <Button
            onClick={() => openContentModal(record, 'view')}
            style={{ marginRight: 3 }}
            title="View Template Content"
          >
            <EyeOutlined />
          </Button>
          <Button
            onClick={() => editTemplate(record)}
            style={{ marginRight: 3 }}
            title="Edit Template Metadata"
            disabled={record.dt_deleted === 'Y'}
          >
            <EditOutlined />
          </Button>
          <Button
            onClick={() => openContentModal(record, 'edit')}
            style={{ marginRight: 3 }}
            title="Edit Template Content"
            disabled={record.dt_deleted === 'Y'}
          >
            <FileTextOutlined />
          </Button>
          {record.dt_deleted === 'N' ? (
            <Popconfirm
              title="Delete template?"
              description="This action will deactivate the template."
              onConfirm={() => deleteTemplate(record, 'D')}
              okText="Yes"
              cancelText="No"
            >
              <Button title="Delete template"><DeleteOutlined /></Button>
            </Popconfirm>
          ) : (
            <>
              <Popconfirm
                title="Restore template?"
                description="This will make the template active again."
                onConfirm={() => deleteTemplate(record, 'A')}
                okText="Yes"
                cancelText="No"
              >
                <Button title="Restore template"><UndoOutlined /></Button>
              </Popconfirm>
              <Popconfirm
                title="Delete permanently?"
                description="This action cannot be undone. The file will be removed."
                onConfirm={() => deleteTemplate(record, 'X')}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button danger title="Delete permanently"><DeleteOutlined /></Button>
              </Popconfirm>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <ErrorBoundary>
      <Spin
        spinning={templatesList?.loading || deleteInProgress}
        tip={deleteInProgress ? 'Deleting template...' : 'Loading templates...'}
      >
        <div className="templates-oodocs-container admin-container">
          {templatesList.error && (
            <div className="error-container">
              <strong>Error:</strong> {templatesList.error}
              <Button type="link" size="small" onClick={getTemplatesList} className="retry-button">Retry</Button>
            </div>
          )}
          <div>
            <Card bordered={false} style={{ width: '100%', margin: '1rem auto' }} className="admin-card">
              <div className="template-oodocs-header admin-header">
                <div className="page-title admin-page-title">TEMPLATES (DOCX)</div>
                {isHOD && (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => setAddTemplateModalVisible(true)}
                    style={{ marginRight: 16 }}
                  >
                    Add Template
                  </Button>
                )}
                <div className="template-oodocs-filters-inline admin-filters-inline">
                  <Search
                    placeholder="Search by template name or display name"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300, marginRight: 16 }}
                    prefix={<SearchOutlined />}
                  />
                  <Select
                    placeholder="Filter by Modality"
                    value={selectedModality}
                    onChange={setSelectedModality}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    {modalitiesList.map((m: any) => (
                      <Option key={m.code || m.value} value={m.code || m.value}>{m.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder={selectedModality ? 'Filter by Body Part' : 'Select modality first'}
                    value={selectedBodyPart}
                    onChange={setSelectedBodyPart}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                    disabled={!selectedModality}
                    notFoundContent={selectedModality ? 'No body parts for selected modality' : 'Select a modality first'}
                  >
                    {filteredBodyPartsList.map((bp: any) => (
                      <Option key={bp.code || bp.value} value={bp.code || bp.value}>{bp.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Filter by Status"
                    value={selectedActive}
                    onChange={setSelectedActive}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    <Option key="All" value="">All</Option>
                    <Option key="Active" value="N">Active</Option>
                    <Option key="InActive" value="Y">Inactive</Option>
                  </Select>
                  <Button onClick={clearFilters}>Clear Filters</Button>
                </div>
              </div>

              <Table
                className="template-oodocs-list-table admin-table"
                columns={TEMPLATE_COLUMNS}
                dataSource={filteredTemplates}
                rowKey={(record: any) => record.dt_id}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
                }}
              />
            </Card>
          </div>
        </div>

        {/* Add Template Modal */}
        <Modal
          title="Add Template (.docx)"
          open={addTemplateModalVisible}
          onCancel={() => setAddTemplateModalVisible(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          <AddTemplateOodocs
            onSuccess={() => {
              setAddTemplateModalVisible(false);
              getTemplatesList();
            }}
          />
        </Modal>

        {/* Template Content View/Edit Modal */}
        <TemplateContentModal
          visible={contentModalVisible}
          templateData={selectedTemplate}
          mode={contentModalMode}
          onClose={() => setContentModalVisible(false)}
          onSuccess={() => {
            setContentModalVisible(false);
            getTemplatesList();
          }}
        />

        {/* Edit Template Modal */}
        <EditTemplateOodocs
          visible={editModalVisible}
          templateData={selectedTemplate}
          modalitiesList={modalitiesList}
          bodyPartsList={bodyPartsList}
          onCancel={() => setEditModalVisible(false)}
          onSuccess={() => {
            setEditModalVisible(false);
            getTemplatesList();
          }}
        />

        {/* Preview Template Modal */}
        <Modal
          title="Template Preview"
          open={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => selectedTemplate && handleDownloadTemplate(selectedTemplate)}>
              Download .docx
            </Button>,
            <Button key="close" onClick={() => setPreviewModalVisible(false)}>Close</Button>,
          ]}
          width={600}
        >
          {selectedTemplate && (
            <div className="template-oodocs-preview">
              <div className="preview-meta">
                <p><strong>Template Name:</strong> {(selectedTemplate as any).dt_template_name}</p>
                <p><strong>Display Name:</strong> {(selectedTemplate as any).dt_display_name}</p>
                <p><strong>Modality:</strong> {getModalityLabel((selectedTemplate as any).dt_modality)}</p>
                <p><strong>Body Part:</strong> {getBodyPartLabel((selectedTemplate as any).dt_bodypart)}</p>
                <p><strong>File Size:</strong> {formatFileSize((selectedTemplate as any).dt_file_size)}</p>
                <p><strong>Added By:</strong> {(selectedTemplate as any).dt_added_by || 'N/A'}</p>
                {(selectedTemplate as any).dt_comments && (
                  <p><strong>Comments:</strong> {(selectedTemplate as any).dt_comments}</p>
                )}
                <p><strong>Status:</strong>{' '}
                  <span className={(selectedTemplate as any).dt_deleted === 'N' && (selectedTemplate as any).dt_active === 'Y' ? 'status-active' : 'status-inactive'}>
                    {(selectedTemplate as any).dt_deleted === 'N' && (selectedTemplate as any).dt_active === 'Y' ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          )}
        </Modal>
      </Spin>
    </ErrorBoundary>
  );
};

export default TemplateListOodocs;
